import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ConfirmModal from "../ConfirmModal";
import { confirmSpecialOrder, deliverSpecialOrder } from "../../services";
import {
  SpecialOrderCashPaymentType,
  SpecialOrderDetail,
} from "../../types/special-order.type";
import CancelDialog from "./CancelDialog";
import UndeliveredAlert from "./UndeliveredAlert";
import { useSpecialOrderDocuments } from "./useSpecialOrderDocuments";
import {
  API_DATE_FORMAT,
  PAYMENT_LABEL,
  PAYMENT_OPTIONS,
  activeUndelivered,
  billDetailPath,
  formatOrderDate,
  specialOrderCopyPath,
  specialOrderEditPath,
} from "./data";

interface ActionPanelProps {
  order: SpecialOrderDetail;
  onChanged: () => void;
}

function DocumentButtons({ order }: Readonly<{ order: SpecialOrderDetail }>) {
  const { busy, download, print } = useSpecialOrderDocuments(
    order.uuid,
    order.reserved_transaction_no ?? order.order_no,
  );
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="body2" fontWeight={600}>
        เอกสาร A4
      </Typography>
      <Button
        variant="outlined"
        startIcon={<PrintIcon />}
        loading={busy === "print"}
        disabled={Boolean(busy)}
        onClick={print}
      >
        พิมพ์ทั้งชุด (6 ใบ)
      </Button>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        loading={busy === "customer"}
        disabled={Boolean(busy)}
        onClick={() => download("customer")}
      >
        ดาวน์โหลด PDF ลูกค้า (ใบ 1–3)
      </Button>
      <Button
        variant="text"
        startIcon={<DownloadIcon />}
        loading={busy === "all"}
        disabled={Boolean(busy)}
        onClick={() => download("all")}
      >
        ดาวน์โหลด PDF ทั้งชุด
      </Button>
    </Box>
  );
}

/** The one thing the order is waiting for, and the buttons that move it on. */
export default function ActionPanel({ order, onChanged }: ActionPanelProps) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [deliverOpen, setDeliverOpen] = useState<boolean>(false);
  const [delivering, setDelivering] = useState<boolean>(false);
  const [cancelOpen, setCancelOpen] = useState<boolean>(false);
  const [payment, setPayment] = useState<SpecialOrderCashPaymentType | null>(null);

  const isCash = order.customer.billtype === "cash";
  const isToday = order.delivery_date === dayjs().format(API_DATE_FORMAT);
  const undelivered = activeUndelivered(order);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await confirmSpecialOrder(order.uuid);
      setConfirmOpen(false);
      onChanged();
    } catch {
      // The interceptor shows the API's reason.
    } finally {
      setConfirming(false);
    }
  };

  const handleDeliver = async () => {
    setDelivering(true);
    try {
      await deliverSpecialOrder(order.uuid, isCash && payment ? { payment_type: payment } : {});
      setDeliverOpen(false);
      onChanged();
    } catch {
      // 409 SPECIAL_ORDER_WRONG_DATE / RESERVATION_MISMATCH come back through the interceptor.
    } finally {
      setDelivering(false);
    }
  };

  const cancelButton = (label: string) => (
    <Button variant="outlined" color="error" onClick={() => setCancelOpen(true)}>
      {label}
    </Button>
  );

  return (
    <Paper>
      <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
        ดำเนินการ
      </Typography>

      {order.status === "draft" && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography variant="body2" color="text.secondary">
            ตรวจรายการ จำนวน ราคา และส่วนลดให้ถูกต้องก่อนยืนยัน เมื่อยืนยันสั่งแล้วระบบจะจองเลขบิล
            และแก้ไขจำนวนไม่ได้อีก
          </Typography>
          <Button variant="contained" onClick={() => setConfirmOpen(true)}>
            ยืนยันสั่ง
          </Button>
          <Button variant="outlined" onClick={() => navigate(specialOrderEditPath(order.uuid))}>
            แก้ไขร่าง
          </Button>
          {cancelButton("ยกเลิกออเดอร์")}
        </Box>
      )}

      {order.status === "ordered" && (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <DocumentButtons order={order} />
          <Divider />
          {order.mode === "B" ? (
            <>
              {isCash && (
                <Box>
                  <Typography variant="body2" fontWeight={600} mb={1}>
                    ช่องทางชำระ
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    color="primary"
                    value={payment}
                    onChange={(_, value: SpecialOrderCashPaymentType | null) =>
                      setPayment(value)
                    }
                  >
                    {PAYMENT_OPTIONS.map((option) => (
                      <ToggleButton key={option.value} value={option.value}>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}
              {!isToday && (
                <Alert severity="info">
                  ยืนยันส่งได้ในวันที่ลูกค้ามารับ ({formatOrderDate(order.delivery_date)}) เท่านั้น
                </Alert>
              )}
              <Button
                variant="contained"
                color="success"
                disabled={!isToday || (isCash && !payment)}
                onClick={() => setDeliverOpen(true)}
              >
                ยืนยันส่ง · ลูกค้ารับของแล้ว
              </Button>
            </>
          ) : undelivered ? (
            <UndeliveredAlert undelivered={undelivered} />
          ) : (
            <Alert severity="info">
              รอสายส่ง {order.route.route_code} กด “ยืนยันส่ง” ในแอปเมื่อถึงร้าน
            </Alert>
          )}
          {cancelButton("ยกเลิกออเดอร์")}
        </Box>
      )}

      {order.status === "delivered" && (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Alert severity="success">
            ออกบิล {order.reserved_transaction_no ?? "-"} แล้ว
            {order.payment_type ? ` · ${PAYMENT_LABEL[order.payment_type] ?? order.payment_type}` : ""}
          </Alert>
          {order.billing_uuid && (
            <Button
              variant="outlined"
              startIcon={<ReceiptLongIcon />}
              component={RouterLink}
              to={billDetailPath(order.billing_uuid, order.doc_type)}
            >
              ดูบิลในรายการขาย
            </Button>
          )}
          <DocumentButtons order={order} />
          <Divider />
          {cancelButton("ยกเลิกบิล")}
        </Box>
      )}

      {order.status === "cancelled" && (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Alert severity="error">
            ยกเลิกแล้ว{order.cancel_reason ? ` · ${order.cancel_reason}` : ""}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            ถ้ายังต้องการส่งของ ให้ออกออเดอร์ใหม่ (ระบบคัดลอกรายการเดิมให้ แก้จำนวนและวันที่ได้)
          </Typography>
          <Button variant="contained" onClick={() => navigate(specialOrderCopyPath(order.uuid))}>
            ออกใหม่ (คัดลอกรายการ)
          </Button>
        </Box>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="ยืนยันสั่ง"
        description={`ยืนยันออเดอร์ ${order.order_no} ระบบจะจองเลขบิลและสร้างเอกสาร A4 หลังยืนยันแล้วแก้ไขรายการไม่ได้`}
        loading={confirming}
        confirmLabel="ยืนยันสั่ง"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      <ConfirmModal
        open={deliverOpen}
        title="ยืนยันส่ง"
        description={
          `ลูกค้ารับของตามออเดอร์ ${order.order_no} แล้ว ระบบจะออกบิล ${order.reserved_transaction_no ?? ""} ` +
          `และตัดสต็อกสายพิเศษ${isCash && payment ? ` · ชำระด้วย${PAYMENT_LABEL[payment]}` : ""}`
        }
        loading={delivering}
        confirmLabel="ยืนยันส่ง"
        confirmColor="success"
        onClose={() => setDeliverOpen(false)}
        onConfirm={handleDeliver}
      />

      <CancelDialog
        open={cancelOpen}
        order={order}
        onClose={() => setCancelOpen(false)}
        onCancelled={() => {
          setCancelOpen(false);
          onChanged();
        }}
      />
    </Paper>
  );
}
